import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
} from "react-hook-form";
import Form from "@/components/Form/Form";
import styles from "./BatchTemplateForm.module.scss";
import InputField from "@medoma/components/InputField/InputField";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  FilledButton,
  IconButton,
  PlainButton,
} from "@medoma/components/Button/Button";
import {
  type BatchTemplateActivityFormFields,
  type BatchTemplateFormFields,
  type IBatchTemplateActivityTimeConfigType,
} from "./batchTemplateFormSchema";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import { useEffect, useState } from "react";
import { generateRandomUUID } from "@/Utils/UniqueId";
import {
  BatchTemplateActivityFormContent,
  CreateBatchTemplateActivityForm,
} from "@/pages/template-manager/BatchTemplateForm/BatchTemplateActivityForm/BatchTemplateActivityForm";
import BinIcon from "@medoma/components/icons/BinIcon";
import CaretRightIcon from "@medoma/components/icons/CaretRightIcon";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { Button, Disclosure, DisclosurePanel } from "react-aria-components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBatchTemplate,
  deleteTemplate,
  templatesKeys,
  updateBatchTemplate,
  updateTagsForTemplate,
  batchTemplateQueryOptions,
  type ITemplateTag,
} from "@/api/Templates";
import { Loading } from "@medoma/components/Loading/Loading";
import { Heading } from "@medoma/components/Heading/Heading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { FormFieldPathPrefixContextProvider } from "@/forms/FormFieldPathPrefixContextProvider";
import clsx from "clsx";
import {
  mapEmptyCheckboxFieldsFromFalseToEmptyArray,
  useParentRoutePatientTypes,
} from "../templateManagerUtils";
import { Text } from "@medoma/components/Text/Text";
import { TemplateScheduling } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateScheduling/TemplateScheduling";
import { TemplateFieldValidationContextProvider } from "@/pages/template-manager/TemplateFieldValidationContextProvider";
import { TemplatePatientType } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplatePatientType";
import {
  GroupSelect,
  NO_GROUP_ID,
  NO_PATIENT_TYPE_SELECTED,
  type GroupSelectFields,
} from "../GroupForm/GroupSelect";
import {
  getDefaultGroupId,
  getDefaultCompetenceTagIds,
  getTagIdsFromForm,
} from "../tagUtils";
import {
  CompetenceTagsPicker,
  type CompetenceTagsPickerFields,
} from "../CompetenceTags/CompetenceTagsPicker";

type BatchTemplateFormProps =
  | {
      mode: "create";
      templateId?: never;
      defaultValues?: never;
      tags?: never;
    }
  | {
      mode: "edit";
      templateId: string;
      defaultValues: BatchTemplateFormFields;
      tags: ITemplateTag[];
    };

const BatchTemplateForm = (props: BatchTemplateFormProps) => {
  const { mode, templateId, defaultValues, tags } = props;
  const { t } = useLingui();
  const [
    isAddingActivityWithSharedTimeConfigType,
    setIsAddingActivityWithSharedTimeConfigType,
  ] = useState(false);
  const [
    isAddingActivityWithIndividualTimeConfigType,
    setIsAddingActivityWithIndividualTimeConfigType,
  ] = useState(false);

  const patientTypes = useParentRoutePatientTypes();

  const methods = useForm<
    BatchTemplateFormFields & GroupSelectFields & CompetenceTagsPickerFields
  >({
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          groupId: getDefaultGroupId(tags ?? []),
          competenceTagsIds: getDefaultCompetenceTagIds(tags ?? []),
        }
      : {
          title: "",
          activities: [],
          patientType: patientTypes.length === 1 ? patientTypes[0] : undefined,
          groupId:
            patientTypes.length === 1 ? NO_GROUP_ID : NO_PATIENT_TYPE_SELECTED,
          competenceTagsIds: [],
        },
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
  } = methods;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const selectedPatientType = watch("patientType");
  const currentGroupId = watch("groupId");

  // When a patient type is selected, ensure groupId is never NO_PATIENT_TYPE_SELECTED
  // Reset to NO_GROUP_ID if it's still the placeholder value
  useEffect(() => {
    if (selectedPatientType && currentGroupId === NO_PATIENT_TYPE_SELECTED) {
      setValue("groupId", NO_GROUP_ID);
    }
  }, [selectedPatientType, currentGroupId, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "activities",
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createBatchTemplateMutation, isPending: isPendingCreate } =
    useMutation({
      mutationFn: (
        data: BatchTemplateFormFields &
          GroupSelectFields &
          CompetenceTagsPickerFields,
      ) => {
        const { groupId, competenceTagsIds, ...formData } = data;
        return createBatchTemplate({
          formData,
          tagIds: getTagIdsFromForm(data),
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        reset();
      },
    });

  const { mutate: updateBatchTemplateMutation, isPending: isPendingUpdate } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: BatchTemplateFormFields &
          GroupSelectFields &
          CompetenceTagsPickerFields;
      }) => {
        const { groupId, competenceTagsIds, ...formData } = data;
        await updateBatchTemplate(id, formData);
        await updateTagsForTemplate({ id, tagIds: getTagIdsFromForm(data) });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
      },
    });

  const { mutate: deleteTemplateMutation, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (id: string) => deleteTemplate(id),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        void navigate("..");
      },
    });

  const validateAndSubmit = handleSubmit((data) => {
    const mappedActivities = data.activities.map((activity) =>
      mapEmptyCheckboxFieldsFromFalseToEmptyArray(activity),
    );

    // Map weekdays in shared time config to empty array if false
    const weekdaysAsArray = data.sharedTimeConfig?.weekdays || [];

    const mappedData = {
      ...data,
      activities: mappedActivities,
      sharedTimeConfig: {
        ...data.sharedTimeConfig,
        weekdays: weekdaysAsArray,
      },
    };

    if (mode === "create") {
      createBatchTemplateMutation(mappedData);
    } else {
      updateBatchTemplateMutation({
        id: templateId,
        data: mappedData,
      });
    }
  });

  const isPendingMutation =
    isPendingCreate || isPendingUpdate || isPendingDelete;

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <InputField
          label={t`Titel`}
          showOptionalLabel={false}
          errorMessage={errors.title?.message}
          {...register(`title`, {
            required: {
              value: true,
              message: t`Ange titel`,
            },
          })}
        />

        <TemplateFieldValidationContextProvider
          value={{ isInFinalValidationPhase: false }}
        >
          {patientTypes.length > 1 ? (
            <TemplatePatientType availablePatientTypes={patientTypes} />
          ) : null}
        </TemplateFieldValidationContextProvider>

        <GroupSelect selectedPatientType={selectedPatientType} />
        <CompetenceTagsPicker />

        <section className={styles.section}>
          <Heading level="h2">
            <Trans>Aktiviteter med delad tid</Trans>
          </Heading>
          <Text element="p">
            <Trans>Delar tidskonfigurationen nedan.</Trans>
          </Text>
          <div className={styles.batchTemplateItems}>
            {fields.map((field, index) =>
              field.timeConfigType === "shared" ? (
                <ActivityPanel
                  key={field.id}
                  index={index}
                  remove={remove}
                  timeConfigType="shared"
                  batchPatientType={selectedPatientType}
                />
              ) : null,
            )}
            {isAddingActivityWithSharedTimeConfigType ? (
              <NewActivityPanel
                append={append}
                timeConfigType="shared"
                batchPatientType={selectedPatientType}
                onClose={() =>
                  setIsAddingActivityWithSharedTimeConfigType(false)
                }
              />
            ) : (
              <button
                type="button"
                className={styles.addActivityButton}
                onClick={() =>
                  setIsAddingActivityWithSharedTimeConfigType(true)
                }
              >
                <PlusIcon />
                <Trans>Lägg till aktivitet</Trans>
              </button>
            )}
          </div>
          <FormFieldPathPrefixContextProvider prefix="sharedTimeConfig">
            <div className={styles.sharedTimeConfig}>
              <div className={styles.sharedTimeConfigFields}>
                <TemplateFieldValidationContextProvider
                  value={{ isInFinalValidationPhase: false }}
                >
                  <TemplateScheduling />
                </TemplateFieldValidationContextProvider>
              </div>
            </div>
          </FormFieldPathPrefixContextProvider>
        </section>
        <section className={styles.section}>
          <Heading level="h2">
            <Trans>Aktiviteter med individuell tid</Trans>
          </Heading>
          <Text element="p">
            <Trans>
              Ange tidskonfiguration för varje aktivitet. Följer startdatum som
              väljs vid mallanvändning.
            </Trans>
          </Text>
          <div className={styles.batchTemplateItems}>
            {fields.map((field, index) =>
              field.timeConfigType === "individual" ? (
                <ActivityPanel
                  key={field.id}
                  index={index}
                  remove={remove}
                  timeConfigType="individual"
                  batchPatientType={selectedPatientType}
                />
              ) : null,
            )}
            {isAddingActivityWithIndividualTimeConfigType ? (
              <NewActivityPanel
                append={append}
                timeConfigType="individual"
                batchPatientType={selectedPatientType}
                onClose={() =>
                  setIsAddingActivityWithIndividualTimeConfigType(false)
                }
              />
            ) : (
              <button
                type="button"
                className={styles.addActivityButton}
                onClick={() =>
                  setIsAddingActivityWithIndividualTimeConfigType(true)
                }
              >
                <PlusIcon />
                <Trans>Lägg till aktivitet</Trans>
              </button>
            )}
          </div>
        </section>
        <div className={styles.actions}>
          <FilledButton type="submit" disabled={isPendingMutation}>
            <Trans>Spara</Trans>
          </FilledButton>
          {mode === "edit" ? (
            <PlainButton
              type="button"
              disabled={isPendingMutation}
              onClick={() => deleteTemplateMutation(templateId)}
            >
              <BinIcon />
              <Trans>Ta bort</Trans>
            </PlainButton>
          ) : null}
        </div>
      </Form>
    </FormProvider>
  );
};

const ActivityPanel = ({
  index,
  remove,
  timeConfigType,
  batchPatientType,
}: {
  index: number;
  remove: UseFieldArrayRemove;
  timeConfigType: IBatchTemplateActivityTimeConfigType;
  batchPatientType: BatchTemplateFormFields["patientType"];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLingui();
  const { watch, trigger } = useFormContext<BatchTemplateFormFields>();

  const title = watch(`activities.${index}.title`);
  const category = watch(`activities.${index}.category`);

  const handleDone = async () => {
    const isValid = await trigger(`activities.${index}`);
    if (isValid) {
      setIsExpanded(false);
    }
  };

  return (
    <Disclosure
      className={styles.activityPanel}
      isExpanded={isExpanded}
      onExpandedChange={setIsExpanded}
    >
      <div className={styles.activityPanelHeader}>
        <Button
          slot="trigger"
          className={styles.expandTrigger}
          aria-label={title}
        >
          <CaretRightIcon />
        </Button>
        <span className={styles.headerTitle}>
          <CategoryIcon category={category} size="small" />
          {title}
        </span>
        <IconButton
          aria-label={t`Ta bort ${title}`}
          type="button"
          onClick={() => remove(index)}
        >
          <BinIcon />
        </IconButton>
      </div>
      <DisclosurePanel>
        <section className={styles.activityPanelContent}>
          <FormFieldPathPrefixContextProvider prefix={`activities.${index}`}>
            <BatchTemplateActivityFormContent
              onDone={handleDone}
              timeConfigType={timeConfigType}
              batchPatientType={batchPatientType}
            />
          </FormFieldPathPrefixContextProvider>
        </section>
      </DisclosurePanel>
    </Disclosure>
  );
};

const NewActivityPanel = ({
  append,
  onClose,
  timeConfigType,
  batchPatientType,
}: {
  append: UseFieldArrayAppend<
    BatchTemplateFormFields & GroupSelectFields & CompetenceTagsPickerFields,
    "activities"
  >;
  onClose: () => void;
  timeConfigType: IBatchTemplateActivityTimeConfigType;
  batchPatientType: BatchTemplateFormFields["patientType"];
}) => {
  const handleSave = (data: BatchTemplateActivityFormFields) => {
    append({
      id: generateRandomUUID(),
      ...data,
      timeConfigType,
    });
    onClose();
  };

  return (
    <article className={clsx(styles.activityPanel, styles.newActivityPanel)}>
      <CreateBatchTemplateActivityForm
        onSave={handleSave}
        onCancel={onClose}
        timeConfigType={timeConfigType}
        batchPatientType={batchPatientType}
      />
    </article>
  );
};

export const NewBatchTemplateForm = () => {
  return (
    <>
      <header className={styles.header}>
        <Heading level="h1">
          <Trans>Nytt paket</Trans>
        </Heading>
      </header>
      <BatchTemplateForm key="new" mode="create" />
    </>
  );
};

export const EditBatchTemplateForm = () => {
  const { t } = useLingui();
  const { id } = z.object({ id: z.string() }).parse(useParams());

  const {
    data: template,
    isPending,
    isError,
    error,
  } = useQuery(batchTemplateQueryOptions(id));

  if (isPending) {
    return <Loading message={t`Hämtar mall`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mall.`} ${deducedError(error)}`}
      />
    );
  }

  return (
    <>
      <header className={styles.header}>
        <CategoryIcon category={"MultipleCategories"} />
        <Heading level="h1">{template.template.title}</Heading>
      </header>
      <BatchTemplateForm
        key={id}
        templateId={id}
        defaultValues={template.template}
        tags={template.tags}
        mode="edit"
      />
    </>
  );
};
